/**
 * Связи позиции сметы (СМ-16): комплекты и листы ПСД, по которым посчитан объём, и работы графика,
 * привязанные к позиции. Цены здесь не показываются.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { toast } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

interface Links {
  psd: Array<{
    linkId: string;
    kind: 'set' | 'sheet';
    setId: string;
    setCode: string;
    setName: string;
    sheetId: string | null;
    sheetNumber: string | null;
    sheetName: string | null;
    receivedDate: string | null;
  }>;
  activities: Array<{ id: string; scheduleId: string; code: string; name: string }> | null;
}
interface SetRow {
  id: string;
  code: string;
  name: string;
}
interface SetFull extends SetRow {
  sheets: Array<{ id: string; number: string; name: string }>;
}

const linkCls = 'inline-flex min-h-[44px] items-center text-accent-text underline md:min-h-[28px]';

export function ItemLinks({
  projectId,
  itemId,
  editable,
}: {
  projectId: string;
  itemId: string;
  editable: boolean;
}) {
  const qc = useQueryClient();
  const key = ['estimate-item-links', itemId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<Links>(`/estimate-items/${itemId}/links`),
    retry: false,
  });
  const [adding, setAdding] = useState(false);
  const [setId, setSetId] = useState('');
  const [sheetId, setSheetId] = useState('');
  const sets = useQuery({
    queryKey: ['psd-sets-pick', projectId],
    queryFn: () => api<SetRow[]>('/psd/sets', { query: { projectId } }),
    enabled: adding,
    retry: false,
  });
  const full = useQuery({
    queryKey: ['psd-set-pick', setId],
    queryFn: () => api<SetFull>(`/psd/sets/${setId}`),
    enabled: adding && Boolean(setId),
    retry: false,
  });
  const save = useMutation({
    mutationFn: (b: { toType: 'psd_set' | 'psd_sheet'; toId: string; remove?: boolean }) =>
      api<Links>(`/estimate-items/${itemId}/links`, { body: b }),
    onSuccess: (r) => {
      qc.setQueryData(key, r);
      qc.invalidateQueries({ queryKey: ['psd-set-estimate-items'] });
      setAdding(false);
      setSetId('');
      setSheetId('');
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  if (!q.data) return null;
  const { psd, activities } = q.data;
  return (
    <div className="space-y-1 pb-1 text-[12px]">
      <div className="font-medium text-ink">Связи позиции</div>
      <div className="text-ink-2">
        ПСД:{' '}
        {psd.length ? (
          <ul className="mt-1 space-y-1">
            {psd.map((l) => (
              <li key={l.linkId} className="flex flex-wrap items-center gap-x-2 text-ink">
                <Link to="/psd/sets/$setId" params={{ setId: l.setId }} className={linkCls}>
                  {l.setCode}
                  {l.sheetNumber ? `, лист ${l.sheetNumber}` : ''}
                </Link>
                <span className="text-ink-2 [overflow-wrap:anywhere]">{l.sheetName ?? l.setName}</span>
                {editable ? (
                  <button
                    type="button"
                    className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center text-accent-text underline md:min-h-[28px]"
                    disabled={save.isPending}
                    onClick={() =>
                      save.mutate({
                        toType: l.kind === 'sheet' ? 'psd_sheet' : 'psd_set',
                        toId: l.sheetId ?? l.setId,
                        remove: true,
                      })
                    }
                  >
                    снять
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          'не привязана — объём без ссылки на чертежи'
        )}
      </div>
      {activities ? (
        <div className="text-ink-2">
          График:{' '}
          {activities.length
            ? activities.map((a, i) => (
                <span key={a.id}>
                  {i ? ', ' : ''}
                  <Link to="/schedule/$projectId" params={{ projectId }} className={linkCls}>
                    {a.code} {a.name}
                  </Link>
                </span>
              ))
            : 'ни одна работа не привязана'}
        </div>
      ) : null}
      {editable && !adding ? (
        <button type="button" className={linkCls} onClick={() => setAdding(true)}>
          Привязать к ПСД
        </button>
      ) : null}
      {editable && adding ? (
        <div className="flex flex-wrap items-end gap-2">
          <Select
            className="min-h-[44px] md:min-h-0"
            aria-label="Комплект ПСД"
            value={setId}
            onChange={(e) => {
              setSetId(e.target.value);
              setSheetId('');
            }}
          >
            <option value="">— комплект —</option>
            {(sets.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} — {s.name}
              </option>
            ))}
          </Select>
          <Select
            className="min-h-[44px] md:min-h-0"
            aria-label="Лист комплекта"
            value={sheetId}
            disabled={!setId}
            onChange={(e) => setSheetId(e.target.value)}
          >
            <option value="">весь комплект</option>
            {(full.data?.sheets ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                лист {s.number} — {s.name}
              </option>
            ))}
          </Select>
          <Button
            className="min-h-[44px] md:min-h-0"
            disabled={!setId || save.isPending}
            onClick={() =>
              save.mutate(
                sheetId ? { toType: 'psd_sheet', toId: sheetId } : { toType: 'psd_set', toId: setId },
              )
            }
          >
            Привязать
          </Button>
          <Button variant="ghost" className="min-h-[44px] md:min-h-0" onClick={() => setAdding(false)}>
            Отмена
          </Button>
        </div>
      ) : null}
    </div>
  );
}

interface SetItem {
  id: string;
  estimateId: string;
  estimateCode: string;
  number: number;
  code: string | null;
  name: string;
  unit: string;
  quantity: number;
  sheetNumber: string | null;
}

/** В карточке комплекта ПСД: позиции смет, объём которых посчитан по этому комплекту (СМ-16) */
export function SetEstimateItems({ setId }: { setId: string }) {
  const q = useQuery({
    queryKey: ['psd-set-estimate-items', setId],
    queryFn: () => api<SetItem[]>(`/psd-sets/${setId}/estimate-items`),
    retry: false,
  });
  if (!q.data?.length) return null;
  return (
    <Panel title="Позиции смет по этому комплекту">
      <ul className="flex flex-col gap-1 text-[14px]">
        {q.data.map((i) => (
          <li key={i.id} className="[overflow-wrap:anywhere]">
            <Link
              to="/estimates/$estimateId"
              params={{ estimateId: i.estimateId }}
              className="inline-flex min-h-11 items-center hover:underline md:min-h-0"
            >
              {i.estimateCode}, поз. {i.number}
            </Link>{' '}
            <span className="text-ink-2">
              {i.name}
              {i.sheetNumber ? `, лист ${i.sheetNumber}` : ''}
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
